import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Divider,
  Tooltip
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Stop,
  Delete,
  Refresh,
  Add,
  Edit,
  Visibility,
  Cancel,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  TrendingUp
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config/api';

interface QueueItem {
  id: string;
  type: 'smoketest' | 'regression' | 'fulltest';
  userName: string;
  timestamp: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  position: number;
  priority?: number;
  runId?: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
}

interface QueueStatus {
  total: number;
  pending: number;
  running: number;
  completed: number;
  failed: number;
  cancelled: number;
  isProcessing: boolean;
  currentItem?: QueueItem;
}

interface QueueStatistics {
  totalItems: number;
  recentItems: number;
  averageWaitTime: number;
  successRate: number;
  lastUpdated: string;
}

export default function QueueManagement() {
  const { t } = useTranslation();
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [status, setStatus] = useState<QueueStatus | null>(null);
  const [statistics, setStatistics] = useState<QueueStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // 对话框状态
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [priorityDialogOpen, setPriorityDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<QueueItem | null>(null);
  const [newPriority, setNewPriority] = useState<number>(0);

  // 新队列项表单
  const [newItem, setNewItem] = useState({
    type: 'smoketest' as 'smoketest' | 'regression' | 'fulltest',
    userName: '',
    priority: 0,
    configId: '' as string | ''
  });
  const [availableConfigs, setAvailableConfigs] = useState<any[]>([]);

  useEffect(() => {
    fetchQueueStatus();
    fetchConfigs();
    const interval = setInterval(fetchQueueStatus, 5000); // 每5秒刷新
    return () => clearInterval(interval);
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-configs`);
      const data = await response.json();
      if (data.success) {
        setAvailableConfigs(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch configs:', error);
    }
  };

  useEffect(() => {
    // 当测试类型改变时，更新可用的配置列表
    fetchConfigs();
  }, [newItem.type]);

  const fetchQueueStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/queue/status`);
      const data = await response.json();
      
      if (data.success) {
        setQueue(data.data.queue);
        setStatus(data.data.status);
        setStatistics(data.data.statistics);
        setError(null);
      } else {
        setError(data.error || t('queueManagement.messages.fetchFailed'));
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePauseQueue = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/queue/pause`, { method: 'POST' });
      const data = await response.json();
      
      if (data.success) {
        setSnackbar({ open: true, message: t('queueManagement.messages.queuePaused'), severity: 'success' });
        fetchQueueStatus();
      } else {
        setSnackbar({ open: true, message: data.error || t('queueManagement.messages.pauseFailed'), severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleResumeQueue = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/queue/resume`, { method: 'POST' });
      const data = await response.json();
      
      if (data.success) {
        setSnackbar({ open: true, message: t('queueManagement.messages.queueResumed'), severity: 'success' });
        fetchQueueStatus();
      } else {
        setSnackbar({ open: true, message: data.error || t('queueManagement.messages.resumeFailed'), severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleCancelItem = async (itemId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/queue/cancel/${itemId}`, { 
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': 'admin', // 模拟admin用户
          'Accept': 'application/json'
        },
        credentials: 'include' // 🎯 修复：确保发送 cookies
      });
      
      // 🎯 关键修复：检查响应状态
      if (!response.ok) {
        const errorText = await response.text();
        let errorData: { error?: string; details?: string } = {};
        try {
          errorData = JSON.parse(errorText) as { error?: string; details?: string };
        } catch {
          errorData = { error: errorText || `HTTP ${response.status}: ${response.statusText}` };
        }
        const errorMessage = errorData.error || errorData.details || `取消失败: ${response.status} ${response.statusText}`;
        // 🎯 修复：使用标准 Error 构造函数（避免与 MUI Error 图标冲突）
        const errorObj = new (globalThis.Error as typeof Error)(errorMessage);
        throw errorObj;
      }
      
      const data = await response.json() as { success: boolean; error?: string };
      
      if (data.success) {
        setSnackbar({ open: true, message: t('queueManagement.messages.itemCancelled'), severity: 'success' });
        fetchQueueStatus();
      } else {
        setSnackbar({ open: true, message: data.error || t('queueManagement.messages.cancelFailed'), severity: 'error' });
      }
    } catch (err: unknown) {
      console.error('❌ [QueueManagement] 取消队列项失败:', err);
      // 🎯 修复：正确处理 unknown 类型
      let errorMessage = 'Failed to fetch';
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err !== null && err !== undefined) {
        errorMessage = String(err);
      }
      setSnackbar({ open: true, message: `取消失败: ${errorMessage}`, severity: 'error' });
    }
  };

  const handleClearQueue = async () => {
    if (window.confirm(t('queueManagement.messages.confirmClearQueue'))) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/queue/clear`, { method: 'POST' });
        const data = await response.json();
        
        if (data.success) {
          setSnackbar({ open: true, message: t('queueManagement.messages.queueCleared'), severity: 'success' });
          fetchQueueStatus();
        } else {
          setSnackbar({ open: true, message: data.error || t('queueManagement.messages.clearFailed'), severity: 'error' });
        }
      } catch (err: any) {
        setSnackbar({ open: true, message: err.message, severity: 'error' });
      }
    }
  };

  const handleAddItem = async () => {
    try {
      // 🎯 如果选择了配置，加载配置并添加到metadata
      let metadata: any = {};
      if (newItem.configId) {
        const configResponse = await fetch(`${API_BASE_URL}/api/test-configs/${newItem.configId}`);
        const configData = await configResponse.json();
        if (configData.success) {
          metadata = { config: configData.data };
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/queue/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: newItem.type,
          userName: newItem.userName,
          priority: newItem.priority,
          userId: 'admin',
          timestamp: new Date(),
          metadata: Object.keys(metadata).length > 0 ? metadata : undefined
        })
      });
      const data = await response.json();
      
      if (data.success) {
        setSnackbar({ open: true, message: t('queueManagement.messages.itemAdded'), severity: 'success' });
        setAddDialogOpen(false);
        setNewItem({ type: 'smoketest', userName: '', priority: 0, configId: '' });
        fetchQueueStatus();
      } else {
        setSnackbar({ open: true, message: data.error || t('queueManagement.messages.addFailed'), severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleSetPriority = async () => {
    if (!selectedItem) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/queue/priority/${selectedItem.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ priority: newPriority })
      });
      const data = await response.json();
      
      if (data.success) {
        setSnackbar({ open: true, message: t('queueManagement.messages.priorityUpdated'), severity: 'success' });
        setPriorityDialogOpen(false);
        setSelectedItem(null);
        fetchQueueStatus();
      } else {
        setSnackbar({ open: true, message: data.error || t('queueManagement.messages.updateFailed'), severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'default';
      case 'running': return 'primary';
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Schedule />;
      case 'running': return <PlayArrow />;
      case 'completed': return <CheckCircle />;
      case 'failed': return <ErrorIcon />;
      case 'cancelled': return <Cancel />;
      default: return <Schedule />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'smoketest': return t('testConfigManagement.smoketest');
      case 'regression': return t('testConfigManagement.regression');
      case 'fulltest': return t('testConfigManagement.fulltest');
      default: return type;
    }
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      'pending': t('queueManagement.status.pending'),
      'running': t('queueManagement.status.running'),
      'completed': t('queueManagement.status.completed'),
      'failed': t('queueManagement.status.failed'),
      'cancelled': t('queueManagement.status.cancelled')
    };
    return statusMap[status] || status;
  };

  const getConfigName = (config: any) => {
    // 如果是默认配置，使用翻译
    if (config?.isDefault && config.id) {
      const translationKey = `testConfigManagement.defaultConfigs.${config.id}.name`;
      const translated = t(translationKey);
      // 如果翻译存在且不是翻译键本身，使用翻译
      if (translated !== translationKey) {
        return translated;
      }
    }
    return config?.name || '';
  };

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}${t('queueManagement.duration.hours')}${minutes % 60}${t('queueManagement.duration.minutes')}`;
    } else if (minutes > 0) {
      return `${minutes}${t('queueManagement.duration.minutes')}${seconds % 60}${t('queueManagement.duration.seconds')}`;
    } else {
      return `${seconds}${t('queueManagement.duration.seconds')}`;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('queueManagement.title')}
      </Typography>

      {/* 状态概览 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('queueManagement.queueStatus.queueStatus')}
                  </Typography>
                  <Typography variant="h6">
                    {status?.isProcessing ? t('queueManagement.queueStatus.running') : t('queueManagement.queueStatus.paused')}
                  </Typography>
                </Box>
                {status?.isProcessing ? (
                  <PlayArrow color="primary" />
                ) : (
                  <Pause color="disabled" />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('queueManagement.queueStatus.totalItems')}
                  </Typography>
                  <Typography variant="h6">
                    {status?.total || 0}
                  </Typography>
                </Box>
                <TrendingUp color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('queueManagement.queueStatus.pending')}
                  </Typography>
                  <Typography variant="h6">
                    {status?.pending || 0}
                  </Typography>
                </Box>
                <Schedule color="warning" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('queueManagement.queueStatus.successRate')}
                  </Typography>
                  <Typography variant="h6">
                    {statistics?.successRate.toFixed(1) || 0}%
                  </Typography>
                </Box>
                <CheckCircle color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 控制按钮 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="h6" sx={{ mr: 2 }}>
              {t('queueManagement.control.title')}
            </Typography>
            
            {status?.isProcessing ? (
              <Button
                variant="contained"
                color="warning"
                startIcon={<Pause />}
                onClick={handlePauseQueue}
              >
                {t('queueManagement.control.pauseQueue')}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                startIcon={<PlayArrow />}
                onClick={handleResumeQueue}
              >
                {t('queueManagement.control.resumeQueue')}
              </Button>
            )}
            
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setAddDialogOpen(true)}
            >
              {t('queueManagement.control.addItem')}
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={handleClearQueue}
            >
              {t('queueManagement.control.clearQueue')}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchQueueStatus}
            >
              {t('queueManagement.control.refresh')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* 队列列表 */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('queueManagement.columns.position')}</TableCell>
              <TableCell>{t('queueManagement.columns.user')}</TableCell>
              <TableCell>{t('queueManagement.columns.testType')}</TableCell>
              <TableCell>{t('queueManagement.columns.status')}</TableCell>
              <TableCell>{t('queueManagement.columns.priority')}</TableCell>
              <TableCell>{t('queueManagement.columns.startTime')}</TableCell>
              <TableCell>{t('queueManagement.columns.runTime')}</TableCell>
              <TableCell>{t('queueManagement.columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queue.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  {item.status === 'running' ? t('queueManagement.position.running') : 
                   item.status === 'pending' ? `#${item.position}` : 
                   t('queueManagement.position.completed')}
                </TableCell>
                <TableCell>{item.userName}</TableCell>
                <TableCell>{getTypeLabel(item.type)}</TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(item.status)}
                    label={getStatusLabel(item.status)}
                    color={getStatusColor(item.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {item.priority || 0}
                </TableCell>
                <TableCell>
                  {item.startedAt ? new Date(item.startedAt).toLocaleString() : '-'}
                </TableCell>
                <TableCell>
                  {item.startedAt && item.completedAt ? 
                    formatDuration(new Date(item.completedAt).getTime() - new Date(item.startedAt).getTime()) :
                    item.startedAt ? 
                    formatDuration(Date.now() - new Date(item.startedAt).getTime()) :
                    '-'
                  }
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    {(item.status === 'pending' || item.status === 'running') && (
                      <>
                        {item.status === 'pending' && (
                          <Tooltip title={t('queueManagement.actions.setPriority')}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedItem(item);
                                setNewPriority(item.priority || 0);
                                setPriorityDialogOpen(true);
                              }}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={item.status === 'running' ? t('queueManagement.actions.stopTest') : t('queueManagement.actions.cancel')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              // 🎯 修复：只有 running 状态需要确认，且只有用户点击 OK 时才执行
                              if (item.status === 'running') {
                                if (window.confirm(t('queueManagement.messages.confirmStopTest'))) {
                                  handleCancelItem(item.id);
                                }
                                // 用户点击 Cancel 时不执行任何操作
                              } else {
                                // pending 状态直接取消，不需要确认
                                handleCancelItem(item.id);
                              }
                            }}
                          >
                            {item.status === 'running' ? <Stop /> : <Cancel />}
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {item.runId && (
                      <Tooltip title={t('queueManagement.actions.viewDetails')}>
                        <IconButton
                          size="small"
                          onClick={async () => {
                            try {
                              // 获取Run的状态
                              const res = await fetch(`${API_BASE_URL}/api/post-release/run/${item.runId}`);
                              const data = await res.json();
                              
                              if (data.success && data.data) {
                                const run = data.data;
                                const runStatus = run.status;
                                
                                // 如果已完成或失败，跳转到执行记录页面
                                if (runStatus === 'completed' || runStatus === 'failed') {
                                  window.open(`/#/history?runId=${item.runId}`, '_blank');
                                } else {
                                // 如果是running或pending，跳转到PostReleaseTesting页面
                                // 如果有正在运行的item，跳转到对应的执行框
                                const runningItem = run.items?.find((it: any) => it.status === 'running');
                                if (runningItem) {
                                  // 使用query参数传递item信息，用于滚动定位
                                  const itemParam = `${runningItem.deviceId}-${runningItem.integration}-${runningItem.environment}`;
                                  window.open(`/#/post-release-testing?run=${item.runId}&scrollTo=${itemParam}`, '_blank');
                                } else {
                                  // 没有正在运行的item，只跳转到run页面
                                  window.open(`/#/post-release-testing?run=${item.runId}`, '_blank');
                                }
                                }
                              } else {
                                // 如果获取失败，默认跳转到PostReleaseTesting
                                window.open(`/#/post-release-testing?run=${item.runId}`, '_blank');
                              }
                            } catch (error) {
                              console.error('Failed to fetch run status:', error);
                              // 出错时默认跳转到PostReleaseTesting
                              window.open(`/#/post-release-testing?run=${item.runId}`, '_blank');
                            }
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 添加项目对话框 */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('queueManagement.dialog.addItem')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              select
              fullWidth
              label={t('queueManagement.dialog.testType')}
              value={newItem.type}
              onChange={(e) => setNewItem({ ...newItem, type: e.target.value as any, configId: '' })}
              sx={{ mb: 2 }}
              SelectProps={{ native: true }}
            >
              <option value="smoketest">{t('testConfigManagement.smoketest')}</option>
              <option value="regression">{t('testConfigManagement.regression')}</option>
              <option value="fulltest">{t('testConfigManagement.fulltest')}</option>
            </TextField>
            
            {/* 🎯 配置选择 */}
            <TextField
              select
              fullWidth
              label={t('queueManagement.dialog.testConfig')}
              value={newItem.configId}
              onChange={(e) => setNewItem({ ...newItem, configId: e.target.value })}
              sx={{ mb: 2 }}
              SelectProps={{ native: true }}
              helperText={t('queueManagement.dialog.testConfigHint')}
            >
              <option value="">{t('queueManagement.dialog.useDefaultConfig')}</option>
              {availableConfigs
                .filter(c => c.type === newItem.type)
                .map((config) => (
                  <option key={config.id} value={config.id}>
                    {getConfigName(config)} {config.isDefault ? `(${t('queueManagement.dialog.default')})` : ''}
                  </option>
                ))}
            </TextField>
            
            <TextField
              fullWidth
              label={t('queueManagement.dialog.userName')}
              value={newItem.userName}
              onChange={(e) => setNewItem({ ...newItem, userName: e.target.value })}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label={t('queueManagement.dialog.priority')}
              type="number"
              value={newItem.priority}
              onChange={(e) => setNewItem({ ...newItem, priority: parseInt(e.target.value) || 0 })}
              helperText={t('queueManagement.dialog.priorityHint')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDialogOpen(false);
            setNewItem({ type: 'smoketest', userName: '', priority: 0, configId: '' });
          }}>{t('queueManagement.dialog.cancel')}</Button>
          <Button onClick={handleAddItem} variant="contained">{t('queueManagement.dialog.add')}</Button>
        </DialogActions>
      </Dialog>

      {/* 设置优先级对话框 */}
      <Dialog open={priorityDialogOpen} onClose={() => setPriorityDialogOpen(false)}>
        <DialogTitle>{t('queueManagement.dialog.setPriority')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography gutterBottom>
              {t('queueManagement.dialog.itemLabel')}: {selectedItem?.userName} - {getTypeLabel(selectedItem?.type || '')}
            </Typography>
            <TextField
              fullWidth
              label={t('queueManagement.dialog.priority')}
              type="number"
              value={newPriority}
              onChange={(e) => setNewPriority(parseInt(e.target.value) || 0)}
              helperText={t('queueManagement.dialog.priorityHint')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriorityDialogOpen(false)}>{t('queueManagement.dialog.cancel')}</Button>
          <Button onClick={handleSetPriority} variant="contained">{t('queueManagement.dialog.set')}</Button>
        </DialogActions>
      </Dialog>

      {/* 错误提示 */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 成功/错误提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
